/* eslint-disable react/prop-types */
import React from "react"
import Immutable from "immutable"

// Library Components
import { FormControl } from "react-bootstrap"

import { AbstractEnumSelect, AbstractEnumSelectObjectOptions } from "selects/containers/EnumSelect"
import CustomTagSelect from "selects/components/CustomTagSelect"
import ColumnSearchNumberRange from "sheets/components/ColumnSearchNumberRange"
import ColumnSearchDateRangePicker from "sheets/components/columns/ColumnSearchDateRangePicker"
import ColumnEnumFieldSelect from "sheets/components/columns/ColumnEnumFieldSelect"
import ColumnSearchSliderRange from "sheets/components/columns/ColumnSearchSliderRange"
import ColumnSearchGridToggle from "sheets/components/columns/ColumnSearchGridToggle"

// helper functions
import { searchByEnum } from "sheets/helper-functions"

export const ColumnSearchInput = ({ value, search, ...props }) => (
    <FormControl
        autoFocus // eslint-disable-line jsx-a11y/no-autofocus
        data-cy="column-search-input"
        onChange={(e) => search(e.target.value)}
        placeholder="Type to search..."
        value={value}
    />
)

const makeColumnCustomTagSelect =
    (options, multi = true) =>
    ({ value, search, ...props }) => (
        <CustomTagSelect
            allowExpandUp
            value={value}
            options={options.toJS()}
            multi={multi}
            onChange={(item) => search(searchByEnum(item))}
        />
    )

const makeAlignmentTypeColumnEnumSelect =
    (options) =>
    ({ value, search }) => (
        <AbstractEnumSelect value={value} options={options} onChange={(item) => search(searchByEnum(item))} />
    )

// TODO: Fix bug where selected enum choice disappears when you change the value in the shown results
export const makeColumnEnumSelect =
    (options) =>
    ({ value, search, columnData, ...props }) => (
        <AbstractEnumSelectObjectOptions
            value={value}
            multi={true}
            filteredOptions={
                columnData
                    ? options.filter_enums_by_values(
                          Immutable.Set(columnData.toJS ? columnData.toArray() : Object.values(columnData)),
                      )
                    : options.items()
            }
            onChange={(item) => search(searchByEnum(item))}
        />
    )

export const makeColumnBooleanSelect =
    () =>
    ({ value, search, column }) => <ColumnSearchGridToggle value={value} search={search} column={column} />

/**
 * This function returns a React Component based on the field / columnDataType values
 * @name fieldTypeToComponent
 * @function
 * @param {Number} field - If rendering a search trigger for a FieldColumn in sheets or resource tables, the object representing the DjangIO field. This is used to automatically generate the appropriate filtering, sorting, and display infrastructure
 * @param {Number} columnDataType - an int representation of a columnDataType Enum
 * @returns {React.Compoentn}
 */
export const fieldTypeToComponent = (field, columnDataType) => {
    if (field && field._is_enum_field) {
        return ColumnEnumFieldSelect
    } else if (
        columnDataType &&
        columnDataType !== DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.field.value
    ) {
        return columnDataTypeToComponent(columnDataType)
    } else {
        switch (field && field.field_type) {
            case DjangIO.djangio.types.FieldType.BooleanField.value:
            case DjangIO.djangio.types.FieldType.NullBooleanField.value:
                return ColumnSearchGridToggle
            case DjangIO.djangio.types.FieldType.IntegerField.value:
                return ColumnSearchNumberRange
            case DjangIO.djangio.types.FieldType.DateTimeField.value:
            case DjangIO.djangio.types.FieldType.QuorumDateTimeField.value:
            case DjangIO.djangio.types.FieldType.DateField.value:
                return ColumnSearchDateRangePicker
            default:
                return ColumnSearchInput
        }
    }
}

/**
 * This function returns a React Component based on a columnDataType passed in
 * @name columnDataTypeToComponent
 * @function
 * @param {Number} columnDataType - a member of the ColumnDataType enum representing the data type of column
 * @returns {React.Component}
 */
export const columnDataTypeToComponent = (columnDataType) => {
    switch (columnDataType) {
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.boolean.value:
            return ColumnSearchGridToggle
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.date.value:
            return ColumnSearchDateRangePicker
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.integer.value:
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.currency.value:
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.long.value:
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.year.value:
            return ColumnSearchNumberRange
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.decimal.value:
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.percent.value:
            return ColumnSearchSliderRange
        default:
            return ColumnSearchInput
    }
}

/**
 * This function returns a React Component based on a customTag passed in
 * @name customTagToComponent
 * @function
 * @param {Object} customTag - CustomTag object. Used to render the filtering component correctly
 *                          depending on the type of the custom field and its options
 */
const customTagToComponent = (customTag) => {
    switch (customTag.get("tag_type")) {
        case DjangIO.app.person.types.TagType.number.value:
            return ColumnSearchSliderRange
        case DjangIO.app.person.types.TagType.string.value:
            return ColumnSearchInput
        case DjangIO.app.person.types.TagType.date.value:
            return ColumnSearchDateRangePicker
        case DjangIO.app.person.types.TagType.single_option_list.value:
            return makeColumnCustomTagSelect(customTag.get("options_new"))
        case DjangIO.app.person.types.TagType.multi_options_list.value:
            return makeColumnCustomTagSelect(customTag.get("options_new", false))
        case DjangIO.app.person.types.TagType.boolean.value:
            return ColumnSearchGridToggle
    }
}

/**
 * This function generates the component used to filter a column when a user clicks on the filter button on a column
 * @name searchTriggerForColumnType
 * @function
 * @param {Number} columnType - a member of the SheetColumnType enum representing the type of column
 * @param {Number} columnDataType - a member of the ColumnDataType enum representing the data type of column
 * @param {object} field - If rendering a search trigger for a FieldColumn in sheets or resource tables, the object representing the DjangIO field. This is used to automatically generate the appropriate filtering, sorting, and display infrastructure
 * @param {Number} customTag - a member of the TagType enum. Used to render the filtering component correctly depending on the type of the custom field
 * @param {object} alignmentTypes - a collection of AlignmentTypes. Used to render the filtering component as though AlignmentTypes were a static enum
 * @returns {React.Component} - A component used to filter a column
 */
export const searchTriggerForColumnType = (columnType, columnDataType, field = {}, customTag, alignmentTypes) => {
    switch (columnType) {
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_field_from_note.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_tag.value:
            return customTagToComponent(customTag)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.staffer_field.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.field.value:
            return fieldTypeToComponent(field, columnDataType)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.region_abbrev_bill.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.region_abbrev_person.value:
            return ColumnEnumFieldSelect
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.sponsorship_record.value:
            return makeColumnEnumSelect(DjangIO.app.bill.models.SponsorType)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.note_interaction_type.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type_public_organization.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type_office.value:
            return makeColumnEnumSelect(DjangIO.app.userdata.types.InteractionType)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.committee_membership.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.caucus_membership.value:
            return makeColumnEnumSelect(DjangIO.app.committee.models.CommitteeMemberRole)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.voting_record.value:
            return makeColumnEnumSelect(DjangIO.app.vote.models.VoteResultType)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.stance_record.value:
            return makeColumnEnumSelect(DjangIO.app.userdata.types.SheetStanceVoteResultType)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.bill_priority.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.issue_priority.value:
            return makeColumnEnumSelect(DjangIO.app.userdata.types.LegislativePriorityType)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.bill_stance.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.issue_stance.value:
            return makeColumnEnumSelect(DjangIO.app.userdata.types.LegislativeStanceType)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_thumb.value:
            return makeColumnEnumSelect(DjangIO.app.userdata.spreadsheet.types.ThumbType)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_field.value:
            return columnDataType === DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.decimal.value
                ? ColumnSearchNumberRange
                : ColumnSearchInput
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.rsvp_status_of_custom_event.value:
            return makeColumnEnumSelect(DjangIO.app.custom_event.types.RSVPStatus)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.stakeholder_alignment.value:
            return makeAlignmentTypeColumnEnumSelect(alignmentTypes)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.percentage_of_columns.value:
            return (props) => <ColumnSearchSliderRange {...props} isPercentColumn={true} />
        default:
            return columnDataTypeToComponent(columnDataType)
    }
}
